import { useRouter } from 'next/router'
import * as React from 'react'

import {
  getBrowserRoutePath,
  routePathsMatch,
  routerMatchesTargetUrl,
} from '@/utils/route-transition.utils'

const ROUTE_LOADING_TIMEOUT_MS = 10_000
const ROUTE_STUCK_RECOVERY_MS = 3_000
const ROUTE_RECOVERY_DELAY_MS = 150
const ROUTE_SYNC_POLL_MS = 100

type RouteTransitionOptions = {
  onLoadingChange: (loading: boolean) => void
}

/**
 * Tracks Next.js route transitions for the global loading overlay and recovers
 * when the browser URL updates but the router never finishes (e.g. /my-courses/[slug] → /admin/dashboard).
 */
export function useRouteTransition({ onLoadingChange }: RouteTransitionOptions) {
  const router = useRouter()
  const pendingUrlRef = React.useRef<string | null>(null)
  const recoveryAttemptedRef = React.useRef(false)
  const timeoutIdRef = React.useRef<ReturnType<typeof setTimeout> | undefined>()
  const stuckRecoveryIdRef = React.useRef<ReturnType<typeof setTimeout> | undefined>()
  const [transitionTick, setTransitionTick] = React.useState(0)

  const clearLoadingTimeout = React.useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = undefined
    }
  }, [])

  const clearStuckRecoveryTimeout = React.useCallback(() => {
    if (stuckRecoveryIdRef.current) {
      clearTimeout(stuckRecoveryIdRef.current)
      stuckRecoveryIdRef.current = undefined
    }
  }, [])

  const setLoading = React.useCallback(
    (loading: boolean) => {
      onLoadingChange(loading)
    },
    [onLoadingChange]
  )

  const clearPendingNavigation = React.useCallback(() => {
    pendingUrlRef.current = null
    recoveryAttemptedRef.current = false
    clearStuckRecoveryTimeout()
  }, [clearStuckRecoveryTimeout])

  const stopLoading = React.useCallback(() => {
    clearLoadingTimeout()
    clearStuckRecoveryTimeout()
    setLoading(false)
  }, [clearLoadingTimeout, clearStuckRecoveryTimeout, setLoading])

  const startLoading = React.useCallback(() => {
    setLoading(true)
    clearLoadingTimeout()
    timeoutIdRef.current = setTimeout(stopLoading, ROUTE_LOADING_TIMEOUT_MS)
  }, [clearLoadingTimeout, setLoading, stopLoading])

  const recoverStuckNavigation = React.useCallback((targetUrl: string) => {
    if (recoveryAttemptedRef.current) {
      return
    }
    recoveryAttemptedRef.current = true

    if (typeof window !== 'undefined') {
      window.location.assign(targetUrl)
    }
  }, [])

  const syncPendingNavigation = React.useCallback(() => {
    const pending = pendingUrlRef.current
    if (!pending) {
      return
    }

    const browserPath = getBrowserRoutePath()
    const reachedInBrowser = routePathsMatch(browserPath, pending)
    const reachedInRouter = routePathsMatch(router.asPath, pending)
    const routerMatchesTarget = routerMatchesTargetUrl(router.route, router.pathname, pending)

    if (routerMatchesTarget && (reachedInBrowser || reachedInRouter)) {
      clearPendingNavigation()
      stopLoading()
      return
    }

    if (!reachedInBrowser && !reachedInRouter) {
      return
    }

    stopLoading()
    recoverStuckNavigation(pending)
  }, [clearPendingNavigation, recoverStuckNavigation, router.asPath, router.pathname, router.route, stopLoading])

  React.useEffect(() => {
    const handleStart = (url: string, { shallow }: { shallow?: boolean } = {}) => {
      if (shallow) {
        return
      }
      pendingUrlRef.current = url
      recoveryAttemptedRef.current = false
      startLoading()
      setTransitionTick(tick => tick + 1)

      clearStuckRecoveryTimeout()
      stuckRecoveryIdRef.current = setTimeout(() => {
        if (pendingUrlRef.current !== url) {
          return
        }
        syncPendingNavigation()
      }, ROUTE_STUCK_RECOVERY_MS)
    }

    const handleDone = () => {
      clearPendingNavigation()
      stopLoading()
    }

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleDone)
    router.events.on('routeChangeError', handleDone)

    return () => {
      clearPendingNavigation()
      stopLoading()
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleDone)
      router.events.off('routeChangeError', handleDone)
    }
  }, [clearPendingNavigation, clearStuckRecoveryTimeout, router, startLoading, stopLoading, syncPendingNavigation])

  React.useEffect(() => {
    if (!pendingUrlRef.current) {
      return
    }

    syncPendingNavigation()

    const pollId = window.setInterval(syncPendingNavigation, ROUTE_SYNC_POLL_MS)
    const recoveryTimer = window.setTimeout(syncPendingNavigation, ROUTE_RECOVERY_DELAY_MS)

    return () => {
      window.clearInterval(pollId)
      window.clearTimeout(recoveryTimer)
    }
  }, [router.asPath, router.route, syncPendingNavigation, transitionTick])
}
